/**
 * 格式化工具：金额（微单位字符串，禁止浮点）/ 时间 / 短 ID / 字节。
 */

import { MICRO_PER_DPB, dpbToMicro, toMicro } from '@shared/chain';

/**
 * 微单位 → DPB 显示串。
 * @param micro 微单位字符串（1 DPB = 1_000_000 µ）
 * @param maxFrac 最大小数位（默认 6，末尾 0 会裁掉）
 */
export function formatDpb(micro: string | number | bigint, maxFrac = 6): string {
  const value = toMicro(micro);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const int = abs / MICRO_PER_DPB;
  const frac = (abs % MICRO_PER_DPB).toString().padStart(6, '0').slice(0, Math.min(6, maxFrac));
  const trimmed = frac.replace(/0+$/, '');
  const body = trimmed.length > 0 ? `${int.toString()}.${trimmed}` : int.toString();
  return `${negative ? '-' : ''}${body}`;
}

/** 微单位 → 带单位的展示串，如 `100.5 DPB`。 */
export function formatDpbWithUnit(micro: string | number | bigint, maxFrac = 6): string {
  return `${formatDpb(micro, maxFrac)} DPB`;
}

/** DPB（用户输入的十进制串）→ 微单位字符串；非法输入返回 '0'（解析口径统一走 `dpbToMicro`）。 */
export function parseDpb(input: string): string {
  try {
    return dpbToMicro(input).toString();
  } catch {
    return '0';
  }
}

/** 千分位整数串。 */
export function formatInt(value: number | bigint): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 相对时间：刚刚 / N 分钟前 / N 小时前 / M-D。 */
export function relTime(ts: number, now: number = Date.now()): string {
  const diff = now - ts;
  if (diff < 0) return '刚刚';
  if (diff < 60_000) return '刚刚';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} 天前`;
  const d = new Date(ts);
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

/** 时钟时间 HH:mm（会话列表用）。 */
export function clockTime(ts: number): string {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

/** 短 ID：`did:dpb:0xabcd…1234` / `12D3Koo…abcd`。 */
export function shortId(id: string, head = 8, tail = 6): string {
  if (!id) return '';
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

/** 从 DID 里取地址部分（`did:dpb:0x…` → `0x…`）。 */
export function didToAddress(did: string): string {
  return did.startsWith('did:dpb:') ? did.slice('did:dpb:'.length) : did;
}

/** 字节数 → 人类可读。 */
export function formatBytes(bytes: number): string {
  const n = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Uint8Array → base64。 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** base64 → Uint8Array。 */
export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/** 字节数组 → hex（小写）。 */
export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

/** hex → 字节数组。 */
export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (clean.length % 2 !== 0) throw new Error('hex length must be even');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** 本地时区 dayKey：`YYYY-MM-DD`。 */
export function dayKey(ts: number = Date.now()): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
}
